import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyUploaderModule, FileInfo, FileUploadError, FileUploadStatus } from '@vendasta/galaxy/uploader';
import { Validator } from '@vendasta/shared';
import { Subscription, map, take } from 'rxjs';
import { DynamicComponentSelectorModule } from '../../components/dynamic-component-selector';
import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { ImageUploadService } from '../../services/image-upload.service';

// Interface
export interface ImageBlock {
  url?: string;
  altText?: string;
  imageStyle: 'fullWidth' | 'center' | 'left' | 'right';
  maxWidth?: number;
  link?: string;
}

// Default blank value
export const blankImageBlock: ImageBlock = {
  url: null,
  altText: null,
  imageStyle: 'fullWidth',
  maxWidth: null,
  link: null,
};

@Component({
  selector: 'email-image-block',
  templateUrl: './image-block.component.html',
  styleUrls: ['./image-block.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    GalaxyButtonGroupModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    GalaxyUploaderModule,
    DynamicComponentSelectorModule,
  ],
})
export class ImageBlockComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'email-image-block';
  @Input() content: ImageBlock;
  @Output() isBlockValid = new EventEmitter<boolean>();

  public urlMailtoOrDynamicContentRegex = Validator.URL_MAILTO_OR_DYNAMIC_CONTENT_REGEX;

  @ViewChild('blockInputs') blockInputs: NgForm;

  FileUploadStatus = FileUploadStatus;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    public emailPreview: EmailPreviewRefreshService,
    private readonly alerts: SnackbarService,
    private imageUploadService: ImageUploadService,
  ) {}

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.blockInputs.statusChanges.subscribe((status) => {
        this.isBlockValid.emit(status === 'VALID');
      }),
    );
    this.subscriptions.push(
      this.blockInputs.valueChanges.subscribe(() => {
        this.emailPreview.refresh();
      }),
    );
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  clearImage(): void {
    this.content.url = null;
    this.emailPreview.refresh();
  }

  setImage(file: FileInfo): void {
    this.content.url = file.url;
    this.emailPreview.refresh();
  }

  alertFileErrored($event: FileUploadError): void {
    console.error('Image upload failed', $event);
    this.alerts.openErrorSnack('IMAGE_UPLOAD_FAILURE');
  }

  async openSuggestImageDialog(): Promise<void> {
    await this.imageUploadService.generateAndUploadImages().then((selectedImage) => {
      if (selectedImage) {
        selectedImage
          .pipe(
            take(1),
            map((img) => {
              this.content.url = img;
              this.emailPreview.refresh();
            }),
          )
          .subscribe();
      }
    });
  }
}
